import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { fmt, fmtDate, fmtDateTime } from '../lib/format';
import { ClientPill, InvoicePill } from '../components/Pill';
import { DateInput } from '../components/DateInput';
import type { Client } from '@otter/db';
import { PaymentMethodSchema, type InvoiceStatus, type PaymentMethod, type SepaScheme } from '@otter/shared';

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  transferencia: 'Transferencia',
  domiciliacion: 'Domiciliación',
  tarjeta: 'Tarjeta',
  confirming: 'Confirming',
  efectivo: 'Efectivo',
  otro: 'Otro',
};

export function paymentMethodLabel(v: string | null | undefined): string {
  if (!v) return '—';
  return PAYMENT_METHOD_LABELS[v as PaymentMethod] ?? v;
}

type Props = {
  id: string;
  onClose: () => void;
  onError: (e: unknown) => void;
  onToast: (msg: string, kind?: 'ok' | 'err') => void;
  onChanged: () => void;
};

type Tab = 'datos' | 'emitidas' | 'recibidas' | 'resumen';

export function ClienteFicha({ id, onClose, onError, onToast, onChanged }: Props) {
  const [client, setClient] = useState<Client | null>(null);
  const [stats, setStats] = useState<{
    invoices_issued_count: number;
    invoices_received_count: number;
    total_billed: number;
    total_collected: number;
  } | null>(null);
  const [tab, setTab] = useState<Tab>('datos');
  const [issued, setIssued] = useState<Array<{
    id: string;
    formattedNumber: string | null;
    status: InvoiceStatus;
    issueDate: string | null;
    totalAmount: string;
  }>>([]);
  const [received, setReceived] = useState<Array<{
    id: string;
    supplier: string;
    date: string;
    totalAmount: string;
    category: string;
  }>>([]);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const r = await api<{ client: Client; stats: typeof stats }>(`/admin/clients/${id}`);
        setClient(r.client);
        setStats(r.stats);
      } catch (e) {
        onError(e);
      }
    })();
  }, [id, onError]);

  useEffect(() => {
    if (tab === 'emitidas') {
      void api<{ items: typeof issued }>('/admin/invoices-issued', { query: { clientId: id } })
        .then((r) => setIssued(r.items))
        .catch(onError);
    } else if (tab === 'recibidas') {
      void api<{ items: typeof received }>('/admin/invoices-received', { query: { clientId: id } })
        .then((r) => setReceived(r.items))
        .catch(onError);
    }
  }, [tab, id, onError]);

  if (!client) return <div className="body"><div className="empty">cargando…</div></div>;

  return (
    <div className="body no-pad">
      <div className="ficha-head">
        <div className="iconcli">{client.name.slice(0, 1).toUpperCase()}</div>
        <div className="iden">
          <h2>{client.name}</h2>
          <div className="ddata">
            <span>NIF {client.nif}</span>
            <span>· {client.email ?? '—'}</span>
            <span>· {client.phone ?? '—'}</span>
            <ClientPill status={client.status} />
          </div>
        </div>
        <div className="actions">
          <button className="btn" onClick={onClose}>← Volver</button>
          <button className="btn" onClick={() => setEditing(true)}>Editar</button>
        </div>
      </div>
      <div className="ftabs">
        {(['datos', 'emitidas', 'recibidas', 'resumen'] as Tab[]).map((t) => (
          <button key={t} className={tab === t ? 'on' : ''} onClick={() => setTab(t)}>
            {t}
          </button>
        ))}
      </div>
      <div className="ficha-body">
        {tab === 'datos' && (
          <div className="ficha-datos">
            <div className="row3">
              <Info k="Dirección" v={client.address ?? '—'} />
              <Info k="Ciudad" v={client.city ?? '—'} />
              <Info k="CP" v={client.postalCode ?? '—'} />
              <Info k="País" v={client.country ?? '—'} />
              <Info k="Email" v={client.email ?? '—'} />
              <Info k="Teléfono" v={client.phone ?? '—'} />
            </div>
            <h4 className="ficha-subh">Pago y datos bancarios</h4>
            <div className="row3">
              <Info k="Método de pago" v={paymentMethodLabel(client.paymentMethod)} />
              <Info k="IBAN" v={client.iban ?? '—'} />
              <Info k="BIC / SWIFT" v={client.bic ?? '—'} />
              <Info k="Banco" v={client.bankName ?? '—'} />
              <Info k="Titular" v={client.accountHolder ?? '—'} />
              {client.paymentMethod === 'domiciliacion' && (
                <>
                  <Info k="Ref. mandato SEPA" v={client.sepaMandateRef ?? '—'} />
                  <Info k="Fecha mandato" v={fmtDate(client.sepaMandateDate)} />
                  <Info k="Esquema SEPA" v={client.sepaScheme ?? '—'} />
                </>
              )}
            </div>
            <h4 className="ficha-subh">Condiciones comerciales</h4>
            <div className="row3">
              <Info k="Condiciones pago" v={`Net ${client.paymentTermsDays}`} />
              <Info k="Crédito asignado" v={fmt(client.creditLimit)} />
              <Info k="Última actividad" v={fmtDate(client.lastActivityAt)} />
              <Info k="Creado" v={fmtDateTime(client.createdAt)} />
            </div>
          </div>
        )}
        {tab === 'emitidas' && (
          <div className="card">
            <table className="t">
              <thead>
                <tr>
                  <th>Número</th>
                  <th>Estado</th>
                  <th>Emisión</th>
                  <th className="num">Total</th>
                </tr>
              </thead>
              <tbody>
                {issued.map((i) => (
                  <tr key={i.id}>
                    <td className="mono">{i.formattedNumber ?? '— borrador —'}</td>
                    <td>
                      <InvoicePill status={i.status} />
                    </td>
                    <td className="mono">{fmtDate(i.issueDate)}</td>
                    <td className="num">{fmt(i.totalAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {issued.length === 0 && <div className="empty">sin facturas emitidas</div>}
          </div>
        )}
        {tab === 'recibidas' && (
          <div className="card">
            <table className="t">
              <thead>
                <tr>
                  <th>Proveedor</th>
                  <th>Categoría</th>
                  <th>Fecha</th>
                  <th className="num">Total</th>
                </tr>
              </thead>
              <tbody>
                {received.map((r) => (
                  <tr key={r.id}>
                    <td><b>{r.supplier}</b></td>
                    <td>{r.category}</td>
                    <td className="mono">{fmtDate(r.date)}</td>
                    <td className="num">{fmt(r.totalAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {received.length === 0 && <div className="empty">sin gastos imputados</div>}
          </div>
        )}
        {tab === 'resumen' && stats && (
          <div className="kpis">
            <Stat k="Facturas emitidas" v={stats.invoices_issued_count} />
            <Stat k="Gastos imputados" v={stats.invoices_received_count} />
            <Stat k="Total facturado" v={fmt(stats.total_billed)} />
            <Stat k="Total cobrado" v={fmt(stats.total_collected)} />
          </div>
        )}
      </div>

      {editing && (
        <EditClientModal
          client={client}
          onClose={() => setEditing(false)}
          onSaved={(updated) => {
            setClient(updated);
            setEditing(false);
            onChanged();
            onToast('Cliente actualizado', 'ok');
          }}
          onError={onError}
        />
      )}
    </div>
  );
}

function Info({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <div style={{ color: 'var(--muted)', fontFamily: 'Geist Mono, monospace', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
        {k}
      </div>
      <div>{v}</div>
    </div>
  );
}

function Stat({ k, v }: { k: string; v: string | number }) {
  return (
    <div className="kpi">
      <div className="lbl">{k}</div>
      <div className="v">{v}</div>
    </div>
  );
}

function EditClientModal(props: {
  client: Client;
  onClose: () => void;
  onSaved: (c: Client) => void;
  onError: (e: unknown) => void;
}) {
  const [name, setName] = useState(props.client.name);
  const [nif, setNif] = useState(props.client.nif);
  const [email, setEmail] = useState(props.client.email ?? '');
  const [phone, setPhone] = useState(props.client.phone ?? '');
  const [address, setAddress] = useState(props.client.address ?? '');
  const [city, setCity] = useState(props.client.city ?? '');
  const [postalCode, setPostalCode] = useState(props.client.postalCode ?? '');
  const [country, setCountry] = useState(props.client.country ?? '');
  const [status, setStatus] = useState(props.client.status);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | ''>(
    (props.client.paymentMethod as PaymentMethod | null) ?? '',
  );
  const [iban, setIban] = useState(props.client.iban ?? '');
  const [bic, setBic] = useState(props.client.bic ?? '');
  const [bankName, setBankName] = useState(props.client.bankName ?? '');
  const [accountHolder, setAccountHolder] = useState(props.client.accountHolder ?? '');
  const [sepaMandateRef, setSepaMandateRef] = useState(props.client.sepaMandateRef ?? '');
  const [sepaMandateDate, setSepaMandateDate] = useState(props.client.sepaMandateDate ?? '');
  const [sepaScheme, setSepaScheme] = useState<SepaScheme | ''>(
    (props.client.sepaScheme as SepaScheme | null) ?? '',
  );
  const [paymentTermsDays, setPaymentTermsDays] = useState(props.client.paymentTermsDays);
  const [creditLimit, setCreditLimit] = useState<number>(
    Number.parseFloat(props.client.creditLimit) || 0,
  );
  const [busy, setBusy] = useState(false);
  const isDirectDebit = paymentMethod === 'domiciliacion';

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api<{ client: Client }>(`/admin/clients/${props.client.id}`, {
        method: 'PATCH',
        body: {
          name,
          nif,
          email: email || null,
          phone: phone || null,
          address: address || null,
          city: city || null,
          postalCode: postalCode || null,
          country: country || null,
          status,
          paymentMethod: paymentMethod || null,
          iban: iban || null,
          bic: bic || null,
          bankName: bankName || null,
          accountHolder: accountHolder || null,
          sepaMandateRef: isDirectDebit ? sepaMandateRef || null : null,
          sepaMandateDate: isDirectDebit ? sepaMandateDate || null : null,
          sepaScheme: isDirectDebit ? sepaScheme || null : null,
          paymentTermsDays,
          creditLimit,
        },
      });
      props.onSaved(res.client);
    } catch (e) {
      props.onError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={props.onClose}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3>Editar cliente</h3>
        <div className="field"><label>Nombre</label><input value={name} onChange={(e) => setName(e.target.value)} /></div>
        <div className="field"><label>NIF</label><input value={nif} onChange={(e) => setNif(e.target.value)} /></div>
        <div className="field"><label>Email</label><input value={email} onChange={(e) => setEmail(e.target.value)} /></div>
        <div className="field"><label>Teléfono</label><input value={phone} onChange={(e) => setPhone(e.target.value)} /></div>
        <div className="field"><label>Dirección</label><input value={address} onChange={(e) => setAddress(e.target.value)} /></div>
        <div className="field"><label>Ciudad</label><input value={city} onChange={(e) => setCity(e.target.value)} /></div>
        <div className="field"><label>Código postal</label><input value={postalCode} onChange={(e) => setPostalCode(e.target.value)} /></div>
        <div className="field"><label>País</label><input value={country} onChange={(e) => setCountry(e.target.value)} /></div>
        <div className="field">
          <label>Estado</label>
          <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
            <option value="activo">activo</option>
            <option value="mora">mora</option>
            <option value="inactivo">inactivo</option>
          </select>
        </div>
        <div className="field">
          <label>Método de pago</label>
          <select
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod | '')}
          >
            <option value="">— sin especificar —</option>
            {PaymentMethodSchema.options.map((m) => (
              <option key={m} value={m}>{PAYMENT_METHOD_LABELS[m]}</option>
            ))}
          </select>
        </div>
        <div className="field"><label>IBAN</label><input value={iban} onChange={(e) => setIban(e.target.value)} placeholder="ES00 0000 0000 0000 0000 0000" /></div>
        <div className="field"><label>BIC / SWIFT</label><input value={bic} onChange={(e) => setBic(e.target.value)} /></div>
        <div className="field"><label>Banco</label><input value={bankName} onChange={(e) => setBankName(e.target.value)} /></div>
        <div className="field"><label>Titular de la cuenta</label><input value={accountHolder} onChange={(e) => setAccountHolder(e.target.value)} /></div>
        {isDirectDebit && (
          <>
            <div className="field"><label>Ref. mandato SEPA</label><input value={sepaMandateRef} onChange={(e) => setSepaMandateRef(e.target.value)} /></div>
            <div className="field"><label>Fecha de firma del mandato</label><DateInput value={sepaMandateDate} onChange={setSepaMandateDate} /></div>
            <div className="field">
              <label>Esquema SEPA</label>
              <select value={sepaScheme} onChange={(e) => setSepaScheme(e.target.value as SepaScheme | '')}>
                <option value="">— sin especificar —</option>
                <option value="CORE">CORE (general)</option>
                <option value="B2B">B2B (entre empresas)</option>
              </select>
            </div>
          </>
        )}
        <div className="field">
          <label>Condiciones de pago (días)</label>
          <input
            type="number"
            min={0}
            max={365}
            value={paymentTermsDays}
            onChange={(e) => setPaymentTermsDays(Number.parseInt(e.target.value, 10) || 0)}
          />
          <div className="hint">Net 30 / Net 60 → número de días desde emisión</div>
        </div>
        <div className="field">
          <label>Límite de crédito (€)</label>
          <input
            type="number"
            min={0}
            step="0.01"
            value={creditLimit}
            onChange={(e) => setCreditLimit(Number.parseFloat(e.target.value) || 0)}
          />
        </div>
        <div className="actions">
          <button type="button" className="btn ghost" onClick={props.onClose}>Cancelar</button>
          <button type="submit" className="btn primary" disabled={busy}>Guardar</button>
        </div>
      </form>
    </div>
  );
}
